import { useCallback } from 'react'
import type { ComponentType } from '@/types/schemas/workspace'
import { usePlanConstraint } from '@/context/plan-constraint-hooks'
import { openUpgradePrompt } from '@/features/upgrade/upgrade-prompt-store'
import { trackEvent, EVENTS } from '@/services/analytics'
import {
  COMPONENT_META,
  GATED_COMPONENTS,
  PANEL_LIMITS,
  PLAN_DISPLAY_NAMES,
} from './add-panel-menu-constants'

/**
 * Whether one panel type can be added right now, and why not when it can't.
 *
 * Shared by every "add a panel" surface (the header dropdown, the pane tab
 * strip's "+", the empty-layout menu and the visual Panel Hub) so they can
 * never disagree about what the plan and the per-type ceilings allow.
 */
export interface PanelAvailability {
  type: ComponentType
  /** The plan does not grant this panel's feature. */
  isLocked: boolean
  /** The layout already holds as many of this type as it may. */
  atLimit: boolean
  /** The cap that bit is the plan's, so a refusal can sell the upgrade. */
  planCapBites: boolean
  /** Smaller of the static per-type ceiling and the plan's allowance. */
  effectiveLimit: number
  /** The plan's own allowance for this type (Infinity when the plan has none). */
  planLimit: number
  /**
   * Nothing to pitch: the static per-type ceiling is a product limit no plan
   * lifts, so the control should be disabled rather than clickable. A locked
   * or plan-capped panel stays clickable so the refusal can raise the upgrade
   * prompt (house rule: a locked control must stay clickable).
   */
  disabled: boolean
  /** Trailing badge copy ("2 max", "2 max on Basic") or null when nothing caps it. */
  badgeText: string | null
}

export function usePanelAvailability(panelCounts?: Partial<Record<ComponentType, number>>) {
  const { planTier, hasAccess, scannerPanelsPerLayoutMax } = usePlanConstraint()

  const resolve = useCallback(
    (type: ComponentType): PanelAvailability => {
      const gate = GATED_COMPONENTS[type]
      // The server's granted feature list is authoritative; `requiredTier`
      // only names the plan in the upgrade copy.
      const isLocked = gate != null && !hasAccess(gate.feature)

      // Two caps can bite: the static per-type instance limit, and (scanner
      // only) the plan's scanner-panels-per-layout allowance. The smaller wins.
      const staticLimit = PANEL_LIMITS[type]
      const planLimit = type === 'scanner' ? scannerPanelsPerLayoutMax : Infinity
      const effectiveLimit = Math.min(staticLimit ?? Infinity, planLimit)
      const atLimit = panelCounts != null && (panelCounts[type] ?? 0) >= effectiveLimit
      const planCapBites = atLimit && planLimit < (staticLimit ?? Infinity)

      const badgeText = atLimit
        ? planCapBites
          ? `${planLimit} max on ${PLAN_DISPLAY_NAMES[planTier]}`
          : `${effectiveLimit} max`
        : null

      return {
        type,
        isLocked,
        atLimit,
        planCapBites,
        effectiveLimit,
        planLimit,
        disabled: atLimit && !planCapBites,
        badgeText,
      }
    },
    [hasAccess, panelCounts, planTier, scannerPanelsPerLayoutMax],
  )

  return { resolve, planTier }
}

/**
 * Run the refusal side effects for an add attempt and say whether the add may
 * proceed. A plan cap or a lock raises the upgrade prompt (the dialog tracks
 * its own UPGRADE_PROMPT_SHOWN, so nothing is tracked here); a static ceiling
 * is a silent no. An allowed add is tracked as PANEL_ADDED.
 */
export function requestPanelAdd(availability: PanelAvailability): boolean {
  const { type, atLimit, planCapBites, planLimit, isLocked } = availability
  if (atLimit) {
    if (planCapBites) {
      openUpgradePrompt({
        feature: 'scanner',
        reason: 'limit',
        context: { limit: planLimit, label: COMPONENT_META[type].label },
      })
    }
    return false
  }
  if (isLocked) {
    const gate = GATED_COMPONENTS[type]
    if (gate) openUpgradePrompt({ feature: gate.feature, reason: 'locked' })
    return false
  }
  trackEvent(EVENTS.PANEL_ADDED, { component_type: type })
  return true
}
